import { useMemo, useState } from "react";
import {
  Badge,
  Box,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconTrophy, IconUsers } from "@tabler/icons-react";
import { DataTable, type DataTableColumn } from "mantine-datatable";
import { MatchScoreCard } from "../../MatchScoreCard";
import {
  MatchScoreStatus,
  StepStatus,
  TournamentGameStatus,
} from "../../../../../../gql/graphql";
import {
  type BracketGroup,
  type Game,
  type GameTeamData,
  type Round,
  type Team,
  type TeamScore,
  buildTeamMap,
  getGameTeams,
  toCardTeam,
} from "./bracket-utils";

export interface StepTeamScore {
  teamId: string;
  score: number;
}

interface GroupStageViewProps {
  groups: BracketGroup[];
  teams: Team[];
  scores: TeamScore[];
  stepTeamScores: StepTeamScore[];
  stepStatus?: StepStatus;
  onGameTeamClick?: (gameId: string, teamId: string) => void;
}

interface StandingsRow {
  id: string;
  rank: number;
  name: string;
  wins: number;
  losses: number;
  draws: number;
  totalScore: number;
}

const RankBadge = ({ rank }: { rank: number }) => {
  if (rank === 1) {
    return (
      <Badge
        color="yellow"
        variant="filled"
        size="sm"
        leftSection={<IconTrophy size={12} />}
      >
        1st
      </Badge>
    );
  }
  if (rank === 2) {
    return (
      <Badge color="gray" variant="filled" size="sm">
        2nd
      </Badge>
    );
  }
  if (rank === 3) {
    return (
      <Badge color="orange" variant="filled" size="sm">
        3rd
      </Badge>
    );
  }
  return (
    <Text size="sm" fw={600} c="dimmed">
      #{rank}
    </Text>
  );
};

const RANK_COLUMN: DataTableColumn<StandingsRow> = {
  accessor: "rank",
  title: "Rank",
  width: 70,
  render: (row: StandingsRow) => <RankBadge rank={row.rank} />,
};

const NAME_COLUMN: DataTableColumn<StandingsRow> = {
  accessor: "name",
  title: "Team",
};

const TOTAL_SCORE_COLUMN: DataTableColumn<StandingsRow> = {
  accessor: "totalScore",
  title: "Score",
  width: 80,
  render: (row: StandingsRow) => (
    <Text size="sm" fw={700}>
      {row.totalScore}
    </Text>
  ),
};

const STANDINGS_COLUMNS: DataTableColumn<StandingsRow>[] = [
  RANK_COLUMN,
  NAME_COLUMN,
  {
    accessor: "wins",
    title: "W",
    width: 60,
    render: (row: StandingsRow) => (
      <Text size="sm" fw={600} c="green">
        {row.wins}
      </Text>
    ),
  },
  {
    accessor: "losses",
    title: "L",
    width: 60,
    render: (row: StandingsRow) => (
      <Text size="sm" fw={600} c="red">
        {row.losses}
      </Text>
    ),
  },
  TOTAL_SCORE_COLUMN,
];

const TOTAL_STANDINGS_COLUMNS: DataTableColumn<StandingsRow>[] = [
  RANK_COLUMN,
  NAME_COLUMN,
  TOTAL_SCORE_COLUMN,
];

const StandingsTable = ({
  standings,
  columns,
}: {
  standings: StandingsRow[];
  columns: DataTableColumn<StandingsRow>[];
}) => (
  <DataTable
    withTableBorder
    style={{ borderRadius: "var(--mantine-radius-md)" }}
    scrollAreaProps={{ type: "never" }}
    highlightOnHover
    records={standings}
    columns={columns}
  />
);

export const GroupStageView = ({
  groups,
  teams,
  scores,
  stepTeamScores,
  stepStatus,
  onGameTeamClick,
}: GroupStageViewProps) => {
  const teamMap = useMemo(() => buildTeamMap(teams), [teams]);

  const backendScoreByTeamId = useMemo(
    () => new Map(stepTeamScores.map((row) => [row.teamId, row.score])),
    [stepTeamScores],
  );

  const getGameTeamsForGame = (game: Game): GameTeamData[] =>
    getGameTeams(game, scores, teamMap);

  const computeStandings = (group: BracketGroup): StandingsRow[] => {
    const stats = new Map<
      string,
      { wins: number; losses: number; draws: number; totalScore: number }
    >();

    group.rounds.forEach((round) => {
      round.games.forEach((game) => {
        const gameTeamData = getGameTeamsForGame(game);

        gameTeamData.forEach(
          ({ team, aggregateStatus, totalScore: gameScore }) => {
            const teamId = team?.id;
            if (!teamId) return;

            let entry = stats.get(teamId);
            if (!entry) {
              entry = {
                wins: 0,
                losses: 0,
                draws: 0,
                totalScore: 0,
              };
              stats.set(teamId, entry);
            }

            if (aggregateStatus === MatchScoreStatus.Winner) {
              entry.wins += 1;
            } else if (
              aggregateStatus === MatchScoreStatus.Loser ||
              aggregateStatus === MatchScoreStatus.Forfeit
            ) {
              entry.losses += 1;
            }
            entry.totalScore += gameScore;
          },
        );
      });
    });

    const useBackendScores =
      stats.size > 0 &&
      Array.from(stats.keys()).every((teamId) =>
        backendScoreByTeamId.has(teamId),
      );

    const rows: StandingsRow[] = Array.from(stats.entries()).map(
      ([teamId, s]) => ({
        id: teamId,
        rank: 0,
        name: teamMap.get(teamId)?.name ?? "Unknown",
        wins: s.wins,
        losses: s.losses,
        draws: s.draws,
        totalScore: useBackendScores
          ? (backendScoreByTeamId.get(teamId) ?? s.totalScore)
          : s.totalScore,
      }),
    );

    const sortedRows = useBackendScores
      ? rows.sort((a, b) => b.totalScore - a.totalScore)
      : rows.sort((a, b) => {
          if (b.wins !== a.wins) return b.wins - a.wins;
          if (a.losses !== b.losses) return a.losses - b.losses;
          return b.totalScore - a.totalScore;
        });

    return sortedRows.map((row, idx) => ({ ...row, rank: idx + 1 }));
  };

  const renderGroup = (group: BracketGroup) => {
    const standings = computeStandings(group);
    const sortedRounds = [...group.rounds].sort((a, b) => a.order - b.order);

    return (
      <Box key={group.id} mb="xl">
        {groups.length > 1 && (
          <Box mb="md">
            <div className="wp-bracket-section-header">
              <ThemeIcon size="md" variant="light" color="purple" radius="md">
                <IconUsers size={16} />
              </ThemeIcon>
              <Text
                size="sm"
                fw={700}
                tt="uppercase"
                style={{ letterSpacing: "0.05em" }}
                c="violet"
              >
                {group.name || `Group ${group.id}`}
              </Text>
            </div>
          </Box>
        )}

        <Box mb="lg">
          <StandingsTable standings={standings} columns={STANDINGS_COLUMNS} />
        </Box>

        <MatchGrid
          rounds={sortedRounds}
          standings={standings}
          getGameTeams={getGameTeamsForGame}
          stepStatus={stepStatus}
          onGameTeamClick={onGameTeamClick}
        />
      </Box>
    );
  };

  if (groups.length === 0) {
    return (
      <Stack align="center" py="xl">
        <ThemeIcon size="xl" variant="light" color="dark" radius="xl">
          <IconUsers size={32} />
        </ThemeIcon>
        <Text size="lg" fw={600} c="dimmed">
          No group data available
        </Text>
        <Text size="sm" c="dimmed">
          Generate the step to create groups
        </Text>
      </Stack>
    );
  }

  return <>{groups.map(renderGroup)}</>;
};

const TOTAL_TAB_VALUE = "total";

interface MatchGridProps {
  rounds: Round[];
  standings: StandingsRow[];
  getGameTeams: (game: Game) => GameTeamData[];
  stepStatus?: StepStatus;
  onGameTeamClick?: (gameId: string, teamId: string) => void;
}

const MatchGrid = ({
  rounds,
  standings,
  getGameTeams,
  stepStatus,
  onGameTeamClick,
}: MatchGridProps) => {
  const [activeTab, setActiveTab] = useState<string | null>(
    rounds[0]?.id ?? null,
  );

  if (rounds.length === 0) return null;

  const isClickable = stepStatus === StepStatus.Started && !!onGameTeamClick;

  return (
    <Tabs value={activeTab} onChange={setActiveTab}>
      <Tabs.List>
        {rounds.map((round) => (
          <Tabs.Tab key={round.id} value={round.id}>
            {round.name || `Round ${round.order}`}
          </Tabs.Tab>
        ))}
        <Tabs.Tab value={TOTAL_TAB_VALUE}>Total</Tabs.Tab>
      </Tabs.List>

      {rounds.map((round) => {
        const sortedGames = [...round.games].sort((a, b) => a.order - b.order);

        return (
          <Tabs.Panel key={round.id} value={round.id} pt="md">
            <SimpleGrid cols={2} spacing="md">
              {sortedGames.map((game) => {
                const gameTeams = getGameTeams(game);
                const isLive = game.status === TournamentGameStatus.Started;
                const isCompleted = game.status === TournamentGameStatus.Ended;

                const bestOf =
                  game.matches.length > 1 ? game.matches.length : undefined;
                const onTeamClick = isClickable
                  ? (teamScore: { teamId: string }) =>
                      onGameTeamClick(game.id, teamScore.teamId)
                  : undefined;

                if (gameTeams.length > 2) {
                  const multiTeams = gameTeams.map((gt) => ({
                    teamId: gt.team?.id ?? "",
                    teamName: gt.team?.name ?? "Unknown",
                    score: gt.totalScore,
                    status: gt.aggregateStatus,
                    matchDetails: gt.teamScores.map((s) => ({
                      matchId: s.matchId,
                      score: s.score,
                      status: s.status,
                    })),
                  }));

                  return (
                    <MatchScoreCard
                      key={game.id}
                      matchNumber={game.order}
                      team1={null}
                      team2={null}
                      teams={multiTeams}
                      bestOf={bestOf}
                      isLive={isLive}
                      isCompleted={isCompleted}
                      onTeamClick={onTeamClick}
                    />
                  );
                }

                return (
                  <MatchScoreCard
                    key={game.id}
                    matchNumber={game.order}
                    team1={toCardTeam(gameTeams[0] ?? null)}
                    team2={toCardTeam(gameTeams[1] ?? null)}
                    bestOf={bestOf}
                    isLive={isLive}
                    isCompleted={isCompleted}
                    onTeamClick={onTeamClick}
                  />
                );
              })}
            </SimpleGrid>
          </Tabs.Panel>
        );
      })}

      <Tabs.Panel value={TOTAL_TAB_VALUE} pt="md">
        <StandingsTable
          standings={standings}
          columns={TOTAL_STANDINGS_COLUMNS}
        />
      </Tabs.Panel>
    </Tabs>
  );
};
